/**
 * The seam.
 *
 * Every surface in this app reads and writes through `src/data/`, and nothing
 * above it knows the data is in memory. When the meeting-notes API is wired in,
 * these modules are what get replaced — one function at a time, without a
 * component changing.
 *
 * Two properties are deliberate:
 *
 *  - **Seeded, not random.** A reload shows the same timeline, which is the
 *    difference between "scrolling worked" and "scrolling probably worked".
 *  - **Latency is simulated.** Every call resolves on a timer, so loading and
 *    empty states are exercised in normal use rather than only when someone
 *    remembers to throttle the network.
 */

export const LATENCY_MS = 220

export function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS))
}

/** mulberry32 — small, seeded, and good enough for fixtures. */
export function seeded(seed: number): () => number {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function pick<T>(random: () => number, items: readonly T[]): T {
  return items[Math.floor(random() * items.length)]!
}

/**
 * Ids are counters, not timestamps or randoms.
 *
 * A fixture id that changes between renders makes React remount the row it
 * belongs to, which loses focus mid-typing — the kind of bug that reads as a
 * design problem.
 */
let counter = 0
export function nextId(prefix: string): string {
  counter += 1
  return `${prefix}-${counter}`
}
